import { useVisible } from "@/hooks/useVisible";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useRegisterBottomCtaBar } from "@/store/bottom-cta-bar-store";
import { cn } from "@/libs/utils";
import React, { useEffect, useState } from "react";

/**
 * 이 바는 `laptop:hidden` — **모바일 전용**이다. `laptop` = 1024px(tailwind.config.ts)
 * 이므로 모바일 구간은 그 미만이다. 클래스와 이 쿼리가 어긋나면 데스크톱에서 서브트리가
 * 살아있거나 모바일에서 바가 사라진다.
 */
const MOBILE_ONLY = "(max-width: 1023px)";

type Props = {
  children?: React.ReactNode;
  /**
   * 노출 조건. 미지정이면 종전대로 항상 노출(푸터 도달 시에만 숨김).
   *
   * 디자인 스펙: "각 화면 상단에 위치하던 Add to Bag, Checkout 버튼이 시야에서
   * 밀리는 시점부터 sticky 로 붙는다". 이 값은 **데스크톱 `CompactBuyBar` 와 같은
   * 신호**여야 한다 — `BuyActions` 컨텍스트의 `scrolledPastBuySection`.
   */
  show?: boolean;
};

const ScrollHideButtonByFooter = ({ children, show = true }: Props) => {
  const [footer, setFooter] = useState<HTMLElement | null>(null);

  // ★ **데스크톱에서는 자식을 마운트하지 않는다.**
  //
  // 이 바는 `laptop:hidden` 이라 데스크톱에서 안 보이는데, 아래 주석대로 언마운트도
  // 하지 않으므로 **서브트리가 살아서 부작용을 낸다.** 실측(1440, `/ko` PDP):
  // 네이버페이 SDK 컨테이너가 **2개** 마운트되고, 그중 하나는 `0×0` 이면서
  // SDK 내용(자식 3개)을 갖고 있었다. `Npay.order.create` 는 dispose 가 비공개라
  // **3초 setInterval(혜택 문구 회전)과 resize 리스너가 페이지 수명 내내 돈다** —
  // 보이지도 않는 버튼 때문이다. 딸려오는 `TactsuitButtons` → `TactSuitCard`
  // 서브트리도 함께 뜬다(#5222).
  //
  // ⚠️ 문제는 **"인스턴스가 둘" 자체가 아니라 "보이지 않는 인스턴스"** 다.
  // 컨테이너 id 는 `useId()` 기반이라 인스턴스마다 유일하고(`NaverPayButton.tsx`),
  // 서로의 DOM 이나 payload 를 섞지 않는다. 그래서 **모바일에서 2개인 것은 정상**이다
  // — 인라인 레일과 이 스티키 바 둘 다 보이고 눌리는 실사용 affordance 다.
  // 낭비인 쪽만 없앤다.
  //
  // ⚠️ **래치다 — 한 번 모바일이었으면 계속 마운트를 유지한다.** `isMobile` 을 그대로
  // 쓰면 모바일→데스크톱 리사이즈에서 언마운트가 일어나 아래 주석이 경고하는 그 누수를
  // 되살린다(SDK dispose 불가). 목표는 "데스크톱 세션에서 아예 안 만든다" 이므로
  // 래치로 충분하다.
  //
  // ⚠️ `useMediaQuery` 는 마운트 전 `false` 다(SSR 안전). 즉 **모바일에서도 첫 렌더엔
  // 자식이 없고**, 마운트 effect 가 돈 뒤에 나타난다. 그래도 위치가 튀지 않는 이유는
  // **게이트가 모든 훅 뒤에 있기 때문**이다 — `null` 을 돌려주는 첫 렌더에도
  // `useVisible` 의 effect 가 등록·즉시 실행되므로(그 훅이 mount 시 `handleEvent()`
  // 를 부른다), 바가 실제로 그려질 때 `isVisible` 은 이미 맞는 값이다.
  //
  // ⚠️ **게이트를 훅 앞으로 올리지 말 것.** 훅 순서 위반이기도 하고, 위의 보장이 깨져
  // 바가 잘못된 위치로 한 프레임 나타난다.
  //
  // ⚠️ **종전엔 호출처가 둘이었다.** `BuyTactsuitPageTemplate`(`show` 미지정 → true)이
  //    하이드레이션 전 한 프레임 바가 없는 쪽이었는데, 그 템플릿을 #5705 에서 지웠다.
  //    지금 남은 호출처는 `show` 를 넘긴다:
  //   · PDP(`show={scrolledPastBuyCta}`) — 상단에선 어차피 숨김이라 차이 없음
  //   · `/cart`(`cart-page`) — 같은 형태
  //    즉 「`show` 미지정」 갈래는 지금 소비처가 0이다.
  //
  // ⚠️ 래치는 **렌더 단계**에서 올린다. `useEffect` 안에서 `setState` 하면 커밋 후
  //    추가 렌더가 한 번 더 돌고(`react-hooks` 규칙이 잡는다), 첫 모바일 렌더가
  //    자식 없이 커밋된 뒤에야 마운트된다. 렌더 중 갱신은 React 가 커밋 없이 즉시
  //    재렌더하는 공식 패턴이다(파생 상태 조정).
  const isMobile = useMediaQuery(MOBILE_ONLY);
  const [everMobile, setEverMobile] = useState(false);
  if (isMobile && !everMobile) setEverMobile(true);

  useEffect(() => {
    const footerElement = document.querySelector("footer");
    setFooter(footerElement);
  }, []);

  const { isVisible } = useVisible({
    to: footer,
  });

  const visible = isVisible && show;

  // 떠 있는 요소(쿠폰 배지 등)가 구매 흐름을 방해하지 않도록 이 바의 존재를 알린다(develop).
  //
  // ⚠️ develop 은 `isVisible` 을 넘겼다. 그쪽은 `if (!isVisible) return null` 이라
  // "보인다 = 마운트돼 있다" 였기 때문이다. **이 브랜치는 언마운트하지 않는다**(네이버페이
  // SDK 타이머 누적, 아래 주석) → `isVisible` 만으로는 실제 노출을 뜻하지 않으므로
  // `show` 까지 곱한 `visible` 을 넘긴다. 안 그러면 스크롤 통과 전부터 배지가 물러선다.
  //
  // `everMobile` 도 곱한다 — 데스크톱에서는 자식을 아예 마운트하지 않으므로 바가 없다.
  // (그 store 의 docblock 은 "데스크톱에서도 마운트돼 있으니 소비처가 모바일 한정으로
  //  쓸 것" 이라고 적고 있는데, 이 브랜치는 그 전제를 없앴다. 등록 자체가 정확해진다.)
  useRegisterBottomCtaBar(everMobile && visible);

  if (!everMobile) return null;

  // ⚠️ **언마운트하지 않는다.** 예전엔 `if (!isVisible) return null` 로 서브트리를
  // 통째로 날렸는데, 그 안에 `ProductNaverPayButton` 이 있다. 네이버페이 SDK 는
  // dispose 가 비공개라 재마운트할 때마다 **3초 setInterval(혜택 문구 회전)과
  // window resize 리스너가 그대로 남아 누적**된다(NaverPayButton.tsx 주석 참고).
  // 푸터에서만 토글될 땐 페이지당 한두 번이라 티가 안 났지만, 노출 조건이 붙으면서
  // 스크롤 교차마다 재생성돼 누수가 쌓이게 됐다.
  // → 화면 밖으로 밀고 `inert` 로 상호작용·포커스만 끊는다. 데스크톱
  //   `CompactBuyBar` 가 쓰는 방식과 같다.
  return (
    <div
      inert={!visible}
      className={cn(
        // ⚠️ `inset-x-0` 이 **필수**다. `fixed` 는 `left` 를 안 주면 정적 위치를 쓰므로,
        // 이 컴포넌트를 좌우 패딩/마진이 있는 컨테이너 **안**에서 렌더하면 그만큼 밀리고
        // `w-full`(=뷰포트 폭) 과 겹쳐 반대쪽으로 넘친다. 실측(#5280, 390 뷰포트, /cart):
        // `left 16 / right 406` — 콘텐츠가 본문보다 16 오른쪽이고 화면을 16 넘쳤다.
        // PDP 는 템플릿 레벨(패딩 밖)에서 렌더해 left 가 우연히 0 이었을 뿐이다.
        "fixed inset-x-0 bottom-0 z-30 flex w-full justify-center laptop:hidden",
        // ⚠️ `invisible` 이 **`inert` 미지원 엔진의 폴백**이다(Safari <15.5,
        // Firefox <112, 구형 WebView). 언마운트를 없앤 뒤로 `inert` 가 유일한
        // 가드였는데, 미지원 엔진에선 화면 밖 바가 그대로 탭·클릭·스크린리더에
        // 노출된다 — 언마운트하던 예전보다 나쁘다. `visibility:hidden` 은 전 엔진에서
        // 포커스 가능성과 a11y 트리 노출을 제거한다.
        // `visibility` 를 트랜지션에 포함시켜 **슬라이드아웃이 끝난 뒤** 숨는다
        // (discrete 전환이라 사라질 땐 duration 동안 visible 을 유지).
        "transition-[transform,visibility] duration-300",
        visible ? "translate-y-0" : "invisible translate-y-full",
      )}
    >
      {children}
    </div>
  );
};

export default ScrollHideButtonByFooter;
